import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../api';
import { Button } from '../../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { useApp } from '../../contexts/AppContext';
import { ArrowLeft, Plus, Library, FileText, Music, Image as ImgIcon, Video, Target, Link2, ExternalLink, User } from 'lucide-react';
import { toast } from 'sonner';

const TYPES = [
  { key: 'fiche', label: 'Fiche cours', icon: FileText, color: '#1d4ed8' },
  { key: 'exercice', label: 'Exercice', icon: Target, color: '#cf2031' },
  { key: 'audio', label: 'Audio briefing', icon: Music, color: '#7c3aed' },
  { key: 'photo', label: 'Photo / Schéma', icon: ImgIcon, color: '#16a34a' },
  { key: 'video', label: 'Vidéo', icon: Video, color: '#f59e0b' },
  { key: 'lien', label: 'Lien externe', icon: Link2, color: '#0891b2' },
  { key: 'autre', label: 'Autre', icon: Library, color: '#64748b' },
];
const THEMES = ['', 'R', 'C', 'L', 'U', 'D', 'M', 'P', 'E', 'S'];

export default function Resources() {
  const navigate = useNavigate();
  const { user } = useApp();
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterType, setFilterType] = useState('');
  const [filterTheme, setFilterTheme] = useState('');
  const [showForm, setShowForm] = useState(false);

  const [form, setForm] = useState({ type: 'fiche', title: '', description: '', body_md: '', external_url: '', theme_code: '' });
  const [posting, setPosting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.listCommunityResources({ type: filterType || undefined, theme_code: filterTheme || undefined });
      setList(res.resources || []);
    } catch (e) { toast.error(e.detail || 'Erreur'); }
    finally { setLoading(false); }
  }, [filterType, filterTheme]);

  useEffect(() => { load(); }, [load]);

  const submit = async () => {
    if (form.title.trim().length < 5) return toast.error('Titre trop court');
    if (form.description.trim().length < 10) return toast.error('Description trop courte');
    setPosting(true);
    try {
      const r = await api.createCommunityResource(form);
      toast.success(r.status === 'approved' ? 'Ressource publiée' : 'Soumis pour validation admin');
      setShowForm(false);
      setForm({ type: 'fiche', title: '', description: '', body_md: '', external_url: '', theme_code: '' });
      load();
    } catch (e) { toast.error(e.detail || 'Erreur'); }
    finally { setPosting(false); }
  };

  const fmtDate = (s) => s ? new Date(s).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' }) : '';
  const TypeIcon = (t) => (TYPES.find(x => x.key === t) || TYPES[0]).icon;
  const TypeColor = (t) => (TYPES.find(x => x.key === t) || TYPES[0]).color;

  return (
    <div className="space-y-5" data-testid="resources-page">
      <Button onClick={() => navigate('/reseau')} variant="ghost" size="sm" className="-ml-2">
        <ArrowLeft className="w-4 h-4 mr-1" /> Retour au réseau
      </Button>
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-3xl font-black flex items-center gap-2">
            <Library className="w-7 h-7 text-[hsl(var(--primary))]" /> Banque de ressources
          </h1>
          <p className="text-sm text-muted-foreground mt-1">Fiches, exercices, audios partagés par les enseignants ECSR.</p>
        </div>
        <Button onClick={() => setShowForm(!showForm)} className="mc-btn-primary" data-testid="new-resource-btn">
          <Plus className="w-4 h-4 mr-1.5" /> Proposer une ressource
        </Button>
      </div>

      {showForm && (
        <div className="mc-card p-5 space-y-3" data-testid="new-resource-form">
          <div className="grid sm:grid-cols-2 gap-3">
            <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
              <SelectTrigger className="h-10 text-sm" data-testid="resource-type">
                <SelectValue placeholder="Type de ressource" />
              </SelectTrigger>
              <SelectContent>
                {TYPES.map(t => <SelectItem key={t.key} value={t.key}>{t.label}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select
              value={form.theme_code || 'none'}
              onValueChange={(v) => setForm({ ...form, theme_code: v === 'none' ? '' : v })}
            >
              <SelectTrigger className="h-10 text-sm" data-testid="resource-theme">
                <SelectValue placeholder="Thème" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">Sans thème spécifique</SelectItem>
                {['R', 'C', 'L', 'U', 'D', 'M', 'P', 'E', 'S'].map(t => (
                  <SelectItem key={t} value={t}>Thème {t}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <input type="text" placeholder="Titre" value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm font-semibold" data-testid="resource-title" />
          <textarea placeholder="Description courte" value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} rows={2} className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm" data-testid="resource-description" />
          <textarea placeholder="Contenu détaillé (optionnel - pour fiches courtes)" value={form.body_md} onChange={e => setForm({ ...form, body_md: e.target.value })} rows={4} className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm font-mono" />
          <input type="url" placeholder="URL externe (optionnel - YouTube, PDF en ligne, etc.)" value={form.external_url} onChange={e => setForm({ ...form, external_url: e.target.value })} className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm" data-testid="resource-url" />
          <div className="flex justify-end gap-2">
            <Button onClick={() => setShowForm(false)} variant="outline" size="sm">Annuler</Button>
            <Button onClick={submit} disabled={posting} className="mc-btn-primary" size="sm" data-testid="resource-submit">{posting ? 'Envoi…' : (user?.role === 'admin' ? 'Publier' : 'Soumettre pour validation')}</Button>
          </div>
        </div>
      )}

      <div className="mc-card p-3 flex flex-wrap gap-2">
        <Select
          value={filterType || 'all'}
          onValueChange={(v) => setFilterType(v === 'all' ? '' : v)}
        >
          <SelectTrigger className="h-9 text-xs w-auto min-w-[160px]" data-testid="filter-type">
            <SelectValue placeholder="Tous types" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tous types</SelectItem>
            {TYPES.map(t => <SelectItem key={t.key} value={t.key}>{t.label}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select
          value={filterTheme || 'all'}
          onValueChange={(v) => setFilterTheme(v === 'all' ? '' : v)}
        >
          <SelectTrigger className="h-9 text-xs w-auto min-w-[160px]" data-testid="filter-theme">
            <SelectValue placeholder="Tous thèmes" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tous thèmes</SelectItem>
            {['R', 'C', 'L', 'U', 'D', 'M', 'P', 'E', 'S'].map(t => (
              <SelectItem key={t} value={t}>Thème {t}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {loading ? (
        <div className="mc-card p-10 text-center text-muted-foreground">Chargement…</div>
      ) : list.length === 0 ? (
        <div className="mc-card p-10 text-center text-sm text-muted-foreground" data-testid="resources-empty">
          Aucune ressource publiée pour le moment. Soyez le premier à partager !
        </div>
      ) : (
        <div className="grid md:grid-cols-2 gap-3">
          {list.map(r => {
            const Icon = TypeIcon(r.type);
            const color = TypeColor(r.type);
            return (
              <div key={r.id} className="mc-card p-4" data-testid={`resource-${r.id}`}>
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: `${color}15`, color }}>
                    <Icon className="w-5 h-5" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="font-black text-sm">{r.title}</h3>
                    <p className="text-xs text-muted-foreground mt-1 leading-relaxed">{r.description}</p>
                    {r.body_md && <pre className="text-xs whitespace-pre-wrap font-sans bg-muted/30 rounded p-2 mt-2">{r.body_md}</pre>}
                    {r.external_url && (
                      <a href={r.external_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 mt-2 text-xs font-bold text-[hsl(var(--primary))]">
                        Ouvrir <ExternalLink className="w-3 h-3" />
                      </a>
                    )}
                    <div className="flex items-center gap-2 mt-3 text-[10px] text-muted-foreground">
                      <span className="inline-flex items-center gap-1"><User className="w-3 h-3" /> {r.author_name}</span>
                      <span>·</span>
                      <span>{fmtDate(r.created_at)}</span>
                      {r.theme_code && <span className="font-mono font-bold rounded-full bg-muted px-1.5">Thème {r.theme_code}</span>}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
